import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import moment from "moment";
import dayjs from "dayjs";
import {
  FaClipboardList,
  FaTimes,
  FaGripHorizontal,
  FaEye,
} from "react-icons/fa";
import styles from "./Editmodal.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import {
  fetchCategoryOptions,
  fetchClassOptions,
  fetchCountryOptions,
  fetchOccupationOptions,
  fetchReligionOptions,
  fetchStateOptions,
  GENDER_OPTIONS,
  STATE_TYPES,
} from "../../../../redux/Masterdata";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomFileInput,
  CustomRadioGroup,
  CustomTextInput,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import {
  switchFormLanguage,
  resetFormLanguage,
} from "../../../../redux/languageSlice";

// ==================== TAMIL ↔ ENGLISH VALUE MAP ====================
const TAMIL_ENGLISH_VALUE_MAP = {
  // Gender
  ஆண்: "Male",
  ஆண: "Male",
  ஆ: "Male",
  பெண்: "Female",
  பெண: "Female",
  பெ: "Female",
  மற்றவை: "Other",
  Male: "Male",
  Female: "Female",
  Other: "Other",
  // Yes / No / 0 / 1
  ஆம்: "Yes",
  ஆம: "Yes",
  இல்லை: "No",
  Yes: "Yes",
  No: "No",
  1: "Yes",
  0: "No",
  // Living with
  பெற்றோர்: "parent",
  பெற்றோர: "parent",
  Parent: "parent",
  parent: "parent",
  பாதுகாவலர்: "guardian",
  காப்பாளர்: "guardian",
  Guardian: "guardian",
  guardian: "guardian",
  // Mother tongue / Languages
  தமிழ்: "Tamil",
  ஆங்கிலம்: "English",
  இந்தி: "Hindi",
  தெலுங்கு: "Telugu",
  மலையாளம்: "Malayalam",
  கன்னடம்: "Kannada",
  உருது: "Urdu",
  Tamil: "Tamil",
  English: "English",
  Hindi: "Hindi",
  Telugu: "Telugu",
  Malayalam: "Malayalam",
  Kannada: "Kannada",
  Urdu: "Urdu",
  Others: "Others",
  மற்றவை: "Others",
  // Medium
  "தமிழ் வழி": "Tamil Medium",
  "ஆங்கில வழி": "English Medium",
  தமிழ்வழி: "Tamil Medium",
  ஆங்கிலவழி: "English Medium",
  "Tamil Medium": "Tamil Medium",
  "English Medium": "English Medium",
  TM: "Tamil Medium",
  EM: "English Medium",
  // Course Preferred
  தமிழ: "TM",
  ஆங்கிலம: "EM",
  // Religion
  இந்து: "Hindu",
  Hindu: "Hindu",
  கிறிஸ்தவம்: "Christian",
  கிருத்துவம்: "Christian",
  Christian: "Christian",
  இஸ்லாம்: "Muslim",
  முஸ்லிம்: "Muslim",
  Muslim: "Muslim",
  சீக்கியம்: "Sikh",
  Sikh: "Sikh",
  புத்தம்: "Buddhist",
  Buddhist: "Buddhist",
  சமணம்: "Jain",
  Jain: "Jain",
  // Category / Community
  OC: "OC",
  BC: "BC",
  BCM: "BCM",
  MBC: "MBC",
  DNC: "DNC",
  SC: "SC",
  SCA: "SCA",
  ST: "ST",
  பிற்படுத்தப்பட்டோர்: "BC",
  "மிகவும் பிற்படுத்தப்பட்டோர்": "MBC",
  "பட்டியல் சாதி": "SC",
  "பட்டியல் பழங்குடி": "ST",
  // Nationality
  Indian: "Indian",
  இந்தியன்: "Indian",
  இந்தியா: "Indian",
  // Qualifying exam
  "10ஆம்": "SSLC",
  SSLC: "SSLC",
  HSC: "HSC",
  "CBSE 10th": "CBSE 10th",
  ICSE: "ICSE",
  // Exam board
  "மாநில வாரியம்": "State Board",
  "State Board": "State Board",
  CBSE: "CBSE",
  Matric: "Matric",
  மெட்ரிக்: "Matric",
  // Year of Admission
  முதல்: "First Year",
  "முதல் ஆண்டு": "First Year",
  "First Year": "First Year",
  இரண்டாம்: "Second Year",
  "இரண்டாம் ஆண்டு": "Second Year",
  "Second Year": "Second Year",
  // State names (Tamil → English)
  தமிழ்நாடு: "Tamil Nadu",
  "Tamil Nadu": "Tamil Nadu",
  கேரளா: "Kerala",
  Kerala: "Kerala",
  கர்நாடகா: "Karnataka",
  Karnataka: "Karnataka",
  "ஆந்திரப் பிரதேசம்": "Andhra Pradesh",
  "Andhra Pradesh": "Andhra Pradesh",
  தெலங்கானா: "Telangana",
  Telangana: "Telangana",
  மகாராஷ்டிரா: "Maharashtra",
  Maharashtra: "Maharashtra",
  "உத்தரப் பிரதேசம்": "Uttar Pradesh",
  "Uttar Pradesh": "Uttar Pradesh",
  குஜராத்: "Gujarat",
  Gujarat: "Gujarat",
  ராஜஸ்தான்: "Rajasthan",
  Rajasthan: "Rajasthan",
  பஞ்சாப்: "Punjab",
  Punjab: "Punjab",
  "மேற்கு வங்காளம்": "West Bengal",
  "West Bengal": "West Bengal",
  பீகார்: "Bihar",
  Bihar: "Bihar",
  ஒடிசா: "Odisha",
  Odisha: "Odisha",
  டெல்லி: "Delhi",
  Delhi: "Delhi",
  புதுச்சேரி: "Puducherry",
  Puducherry: "Puducherry",
  கோவா: "Goa",
  Goa: "Goa",
};

/**
 * Try to find a matching option in an options array for a raw value.
 * Checks: static map → exact value → exact label → contains label.
 * Returns the option's value if matched, else "".
 */
const findMatchInOptions = (rawVal, options, matchBy = "value") => {
  if (!rawVal || !options || options.length === 0) return "";
  const strVal = String(rawVal).trim();
  if (!strVal) return "";

  // 1) Try the static map first, then match mapped value against options
  const mapped = TAMIL_ENGLISH_VALUE_MAP[strVal];
  if (mapped) {
    const opt = options.find(
      (o) =>
        o.value &&
        (String(o[matchBy]) === mapped ||
          String(o[matchBy]).toLowerCase() === mapped.toLowerCase() ||
          (o.label && o.label.toLowerCase() === mapped.toLowerCase())),
    );
    if (opt) return opt[matchBy];
  }

  // 2) Exact match on option based on matchBy (string comparison)
  const exactMatch = options.find(
    (o) => o[matchBy] && String(o[matchBy]) === strVal,
  );
  if (exactMatch) return exactMatch[matchBy];

  // 3) Exact match (case-insensitive) on matchBy field
  const lowerVal = strVal.toLowerCase();
  const exactLabel = options.find(
    (o) => o[matchBy] && String(o[matchBy]).toLowerCase() === lowerVal,
  );
  if (exactLabel) return exactLabel[matchBy];

  // 4) Case-insensitive match on label (when matchBy is "value")
  if (matchBy === "value") {
    const labelMatch = options.find(
      (o) => o.label && o.label.toLowerCase() === lowerVal,
    );
    if (labelMatch) return labelMatch[matchBy];
  }

  // 5) Case-insensitive match on value (when matchBy is "label")
  if (matchBy === "label") {
    const valueMatch = options.find(
      (o) => o.value && String(o.value).toLowerCase() === lowerVal,
    );
    if (valueMatch) return valueMatch[matchBy];
  }

  // 6) Contains / partial match on matchBy field
  const containsMatch = options.find(
    (o) =>
      o[matchBy] &&
      o.value &&
      (String(o[matchBy]).toLowerCase().includes(lowerVal) ||
        lowerVal.includes(String(o[matchBy]).toLowerCase())),
  );
  if (containsMatch) return containsMatch[matchBy];

  // 7) Contains / partial match on label (when matchBy is "value")
  if (matchBy === "value") {
    const labelContains = options.find(
      (o) =>
        o.label &&
        o.value &&
        (o.label.toLowerCase().includes(lowerVal) ||
          lowerVal.includes(o.label.toLowerCase())),
    );
    if (labelContains) return labelContains[matchBy];
  }

  return "";
};

/**
 * Normalize a value against specific options (for radio buttons & static dropdowns).
 * If the value (or its mapped English equivalent) matches an option, return the option value.
 * If NO match is found, return "" (empty string) so the field is cleared.
 */
const normalizeValueAgainstOptions = (val, options) => {
  if (!val && val !== 0) return "";
  const strVal = String(val).trim();
  if (!strVal) return "";

  // Check static map first
  const mapped = TAMIL_ENGLISH_VALUE_MAP[strVal];
  if (mapped) {
    const opt = options.find(
      (o) =>
        o.value &&
        (String(o.value) === mapped ||
          String(o.value).toLowerCase() === mapped.toLowerCase()),
    );
    if (opt) return opt.value;
  }

  // Direct match on option value
  const directMatch = options.find(
    (o) => o.value && String(o.value) === strVal,
  );
  if (directMatch) return directMatch.value;

  // Label match
  const labelMatch = options.find(
    (o) => o.label && o.label.toLowerCase() === strVal.toLowerCase(),
  );
  if (labelMatch) return labelMatch.value;

  return "";
};

// ==================== STATIC OPTIONS ====================
const LIVING_WITH_OPTIONS = [
  { value: "", label: "Select" },
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Guardian" },
];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes", disabled: false },
  { value: "No", label: "No", disabled: false },
];

const MEDIUM_OPTIONS = [
  { value: "", label: "Select" },
  { value: "Tamil Medium", label: "TM" },
  { value: "English Medium", label: "EM" },
];

const MOTHER_TONGUE_OPTIONS = [
  { value: "", label: "Select Mother Tongue" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "Others", label: "Others" },
];

const QUALIFYING_EXAM_OPTIONS = [
  { value: "", label: "Select Examination" },
  { value: "SSLC", label: "SSLC" },
  { value: "HSC", label: "HSC" },
  { value: "CBSE 10th", label: "CBSE 10th" },
  { value: "ICSE", label: "ICSE" },
  { value: "Others", label: "Others" },
];

const EXAM_BOARD_OPTIONS = [
  { value: "", label: "Select Examination Board" },
  { value: "State Board", label: "State Board" },
  { value: "CBSE", label: "CBSE" },
  { value: "ICSE", label: "ICSE" },
  { value: "Matric", label: "Matric" },
  { value: "Others", label: "Others" },
];

const COURSE_PREFERRED_OPTIONS = [
  { value: "", label: "Select Course Preferred" },
  { value: "TM", label: "Tamil" },
  { value: "EM", label: "English" },
];

const YEAR_OF_ADMISSION_OPTIONS = [
  { value: "", label: "Select" },
  { value: "First Year", label: "First Year" },
  { value: "Second Year", label: "Second Year" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Select Status" },
  { value: "Proceed", label: "Proceed" },
  { value: "Rejected", label: "Rejected" },
  { value: "Approved", label: "Approved" },
];

// ==================== TAMIL KEYBOARD LAYOUT ====================
const TAMIL_VOWELS = [
  "அ",
  "ஆ",
  "இ",
  "ஈ",
  "உ",
  "ஊ",
  "எ",
  "ஏ",
  "ஐ",
  "ஒ",
  "ஓ",
  "ஔ",
];
const TAMIL_CONSONANTS = [
  "க",
  "ங",
  "ச",
  "ஞ",
  "ட",
  "ண",
  "த",
  "ந",
  "ப",
  "ம",
  "ய",
  "ர",
  "ல",
  "வ",
  "ழ",
  "ள",
  "ற",
  "ன",
  "ஜ",
  "ஷ",
  "ஸ",
  "ஹ",
];
const TAMIL_MODIFIERS = [
  "்",
  "ா",
  "ி",
  "ீ",
  "ு",
  "ூ",
  "ெ",
  "ே",
  "ை",
  "ொ",
  "ோ",
  "ௌ",
];

// ==================== NUMBER-ONLY FIELDS ====================
const NUMBER_ONLY_FIELDS = [
  "mobile_number",
  "aadhaar_no",
  "parent_pincode",
  "guardian_pincode",
  "emis_id",
  "parent_annual_salary",
  "guardian_annual_salary",
  "tamil_mark",
  "english_mark",
  "mathematics_mark",
  "science_mark",
  "social_science_mark",
  "register_number",
];

const MARK_FIELDS = [
  "tamil_mark",
  "english_mark",
  "mathematics_mark",
  "science_mark",
  "social_science_mark",
];

// ==================== EDIT FORM VALIDATION ====================
const validateHSEditForm = (formData, translations) => {
  const errors = {};
  const t = translations?.labels || {};

  const getVal = (field) => {
    if (!field && field !== 0) return "";
    if (typeof field === "object" && field.value !== undefined)
      return field.value;
    return field;
  };

  const requiredMsg = (label) =>
    t?.fieldRequired ? `${label} ${t.fieldRequired}` : `${label} is required`;

  // ── Basic Details ──
  if (!getVal(formData.applicant_name_in_tamil)?.trim()) {
    errors.applicant_name_in_tamil = requiredMsg(
      t?.studentNameTamil || "Student Name (Tamil)",
    );
  }

  if (!getVal(formData.applicant_name)?.trim()) {
    errors.applicant_name = requiredMsg(
      t?.studentNameEnglish || "Student Name (English)",
    );
  }

  if (!getVal(formData.date_of_birth)) {
    errors.date_of_birth = requiredMsg(t?.dateOfBirth || "Date of Birth");
  }

  if (!getVal(formData.email)?.trim()) {
    errors.email = requiredMsg(t?.emailAddress || "Email");
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(getVal(formData.email).trim())) {
      errors.email = t?.invalidEmail || "Please enter a valid email address";
    }
  }

  if (!getVal(formData.mobile_number)?.trim()) {
    errors.mobile_number = requiredMsg(t?.mobileNumber || "Mobile Number");
  } else if (!/^[6-9]\d{9}$/.test(getVal(formData.mobile_number).trim())) {
    errors.mobile_number =
      t?.invalidMobile || "Mobile number must start with 6-9 and be 10 digits";
  }

  if (!getVal(formData.aadhaar_no)?.trim()) {
    errors.aadhaar_no = requiredMsg(t?.aadharNumber || "Aadhaar No.");
  } else if (!/^\d{12}$/.test(getVal(formData.aadhaar_no).trim())) {
    errors.aadhaar_no = t?.invalidAadhaar || "Aadhaar number must be 12 digits";
  }

  if (!getVal(formData.gender)) {
    errors.gender = requiredMsg(t?.gender || "Gender");
  }

  if (!getVal(formData.religion)) {
    errors.religion = requiredMsg(t?.religion || "Religion");
  }

  if (!getVal(formData.caste)?.trim()) {
    errors.caste = requiredMsg(t?.caste || "Caste");
  }

  if (!getVal(formData.community)) {
    errors.community = requiredMsg(t?.category || "Category");
  }

  if (!getVal(formData.nationality)?.trim()) {
    errors.nationality = requiredMsg(t?.nationality || "Nationality");
  }

  if (!getVal(formData.student_native_state)) {
    errors.student_native_state = requiredMsg(t?.state || "State");
  }

  if (!getVal(formData.place_of_birth)?.trim()) {
    errors.place_of_birth = requiredMsg(t?.birthPlace || "Birth Place");
  }

  if (!getVal(formData.community_certificate_path)) {
    errors.community_certificate_path = requiredMsg(
      t.markSheetRequired || "Mark Sheet is required",
      // t?.communityCertificate || "Community Certificate",
    );
  }

  // ── Residential Details ──
  const livingWith = getVal(formData.living_with);
  if (!livingWith) {
    errors.living_with = requiredMsg(t?.areYouResidingWith || "Living With");
  }

  const isParent = livingWith === "parent";
  const isGuardian = livingWith === "guardian";

  if (livingWith) {
    const nameField = isGuardian ? "guardian_name" : "parent_name";
    const occField = isGuardian ? "guardian_occupation" : "parent_occupation";
    const salaryField = isGuardian
      ? "guardian_annual_salary"
      : "parent_annual_salary";
    const addressField = isGuardian ? "guardian_address" : "parent_address";
    const districtField = isGuardian ? "guardian_district" : "parent_district";
    const cityField = isGuardian ? "guardian_city" : "parent_city";
    const stateField = isGuardian ? "guardian_state" : "parent_state";
    const pincodeField = isGuardian ? "guardian_pincode" : "parent_pincode";

    if (!getVal(formData[nameField])?.trim()) {
      errors[nameField] = requiredMsg(t?.fullname || "Name");
    }

    if (!getVal(formData[occField])) {
      errors[occField] = requiredMsg(t?.occupation || "Occupation");
    }

    if (!getVal(formData[salaryField])?.toString().trim()) {
      errors[salaryField] = requiredMsg(t?.annualIncome || "Annual Income");
    }

    if (!getVal(formData[addressField])?.trim()) {
      errors[addressField] = requiredMsg(t?.address || "Address");
    }

    if (!getVal(formData[districtField])?.trim()) {
      errors[districtField] = requiredMsg(t?.district || "District");
    }

    if (!getVal(formData[cityField])?.trim()) {
      errors[cityField] = requiredMsg(t?.taluk || "Taluk");
    }

    if (!getVal(formData[stateField])) {
      errors[stateField] = requiredMsg(t?.state || "State");
    }

    if (!getVal(formData[pincodeField])?.trim()) {
      errors[pincodeField] = requiredMsg(t?.pincode || "Pincode");
    } else if (!/^\d{6}$/.test(getVal(formData[pincodeField]).trim())) {
      errors[pincodeField] = t?.invalidPincode || "Pincode must be 6 digits";
    }
  }

  // ── Academic Details ──
  if (!getVal(formData.qualified_eligibility_exam)) {
    errors.qualified_eligibility_exam = requiredMsg(
      t?.qualifyingExaminationPassed || "Qualifying Examination Passed",
    );
  }

  if (!getVal(formData.exam_board_name)) {
    errors.exam_board_name = requiredMsg(
      t?.nameOfExaminationBoard || "Examination Board",
    );
  }

  // ── Public Examination Marks ──
  if (!getVal(formData.qualified_exam_name)) {
    errors.qualified_exam_name = requiredMsg(
      t?.nameOfExaminationPassed || "Examination Passed",
    );
  }

  if (!getVal(formData.exam_month_and_year)) {
    errors.exam_month_and_year = requiredMsg(
      t?.monthAndYearOfExamination || "Month and Year of Examination",
    );
  }

  if (!getVal(formData.register_number)?.trim()) {
    errors.register_number = requiredMsg(
      t?.examinationRegisterNumber || "Register Number",
    );
  }

  // Mark fields validation
  MARK_FIELDS.forEach((field) => {
    const val = getVal(formData[field]);
    if (val === "" || val === null || val === undefined) {
      const labelMap = {
        tamil_mark: "Tamil Mark",
        english_mark: "English Mark",
        mathematics_mark: "Mathematics Mark",
        science_mark: "Science Mark",
        social_science_mark: "Social Science Mark",
      };
      errors[field] = requiredMsg(t?.[field] || labelMap[field]);
    } else {
      const numVal = parseInt(val);
      if (isNaN(numVal) || numVal < 0 || numVal > 100) {
        errors[field] = t?.invalidMark || "Marks must be between 0 and 100";
      }
    }
  });

  // ── Preferred Course Details ──
  if (!getVal(formData.preferred_study_course)) {
    errors.preferred_study_course = requiredMsg(
      t?.preferredStudyCourse || "Course Preferred",
    );
  }

  if (!getVal(formData.admission_for_which_year)) {
    errors.admission_for_which_year = requiredMsg(
      t?.yearOfAdmission || "Year of Admission",
    );
  }

  if (!getVal(formData.preferred_group_1)) {
    errors.preferred_group_1 = requiredMsg(
      t?.preferredGroup1 || "Preferred Group 1",
    );
  }

  if (!getVal(formData.mother_tongue)) {
    errors.mother_tongue = requiredMsg(t?.motherTongue || "Mother Tongue");
  }

  if (!getVal(formData.last_medium_of_instruction)) {
    errors.last_medium_of_instruction = requiredMsg(
      t?.mediumOfInstructionLeavingSchool || "Medium of Instruction",
    );
  }

  // ── Last Studied Details ──
  if (!getVal(formData.last_studied_class)) {
    errors.last_studied_class = requiredMsg(
      t?.lastStudiedClass || "Last Studied Class",
    );
  }

  if (!getVal(formData.studied_year)) {
    errors.studied_year = requiredMsg(t?.studiedYear || "Year of Study");
  }

  if (!getVal(formData.last_studied_school_name)?.trim()) {
    errors.last_studied_school_name = requiredMsg(
      t?.lastStudiedSchool || "Last Studied School",
    );
  }

  if (!getVal(formData.emis_id)?.trim()) {
    errors.emis_id = requiredMsg(t?.emisNo || "EMIS No.");
  } else if (!/^\d{10}$/.test(getVal(formData.emis_id).trim())) {
    errors.emis_id = t?.invalidEmis || "EMIS number must be 10 digits";
  }

  if (!getVal(formData.reason_for_discontinuing)?.trim()) {
    errors.reason_for_discontinuing = requiredMsg(
      t?.reasonForLeaving || "Reason for Leaving",
    );
  }

  // ── Other Details ──
  if (!getVal(formData.place)?.trim()) {
    errors.place = requiredMsg(t?.place || "Place");
  }

  if (!getVal(formData.student_image)) {
    errors.student_image = requiredMsg(t?.studentImage || "Student Image");
  }

  // ── Feedback required when status is Rejected or Approved ──
  const selectedStatusVal = formData.status?.value
    ? formData.status.value
    : getVal(formData.status);

  if (selectedStatusVal === "Rejected" || selectedStatusVal === "Approved") {
    if (!getVal(formData.feedback)?.trim()) {
      errors.feedback = requiredMsg(t?.feedback || "Feedback");
    }
  }

  return errors;
};

// ==================== INITIAL FORM DATA ====================
const INITIAL_FORM_DATA = {
  date: moment().format("YYYY-MM-DD"),
  applicant_name_in_tamil: "",
  applicant_name: "",
  date_of_birth: null,
  aadhaar_no: "",
  emis_id: "",
  email: "",
  mobile_number: "",
  student_image: "",
  student_image_original_name: "",
  gender: "",
  nationality: "Indian",
  student_native_state: "",
  place_of_birth: "",
  religion: "",
  caste: "",
  community: "",
  community_certificate_path: "",
  community_certificate_original_name: "",
  // Parent
  living_with: "",
  parent_name: "",
  parent_occupation: "",
  parent_annual_salary: "",
  parent_address: "",
  parent_district: "",
  parent_city: "",
  parent_state: "",
  parent_pincode: "",
  // Guardian
  guardian_name: "",
  guardian_occupation: "",
  guardian_annual_salary: "",
  guardian_address: "",
  guardian_district: "",
  guardian_city: "",
  guardian_state: "",
  guardian_pincode: "",
  // Academic Details
  qualified_eligibility_exam: "",
  exam_board_name: "",
  // Public Exam Marks
  qualified_exam_name: "",
  exam_month_and_year: null,
  register_number: "",
  tamil_mark: "",
  english_mark: "",
  mathematics_mark: "",
  science_mark: "",
  social_science_mark: "",
  total_mark: 0,
  total_percentage: 0,
  // Preferred Course
  preferred_study_course: "",
  admission_for_which_year: "",
  preferred_group_1: "",
  preferred_group_2: "",
  preferred_group_3: "",
  preferred_group_1_medium: null,
  preferred_group_2_medium: null,
  preferred_group_3_medium: null,
  mother_tongue: "",
  last_medium_of_instruction: "",
  // Last Studied
  last_studied_class: "",
  studied_year: null,
  last_studied_school_name: "",
  reason_for_discontinuing: "",
  // Other Details
  willing_to_join_services: "No",
  is_vaccinated_pox: "Yes",
  certificate_path: "",
  certificate_original_name: "",
  place: "",
  // Status Details
  assigned_class: "",
  assigned_group: "",
  assigned_section: "",
  change_section: "",
  current_status: "",
  status: "",
  feedback: "",
  ocr_upload_path: null,
};

const HigherSecondaryAdmissionEditModal = ({
  open,
  onClose,
  admissionId,
  data: rowData,
  fetchAdmissionList,
}) => {
  const dispatch = useDispatch();
  // const { translations, language } = useSelector((state) => state.language);
  // const t = useMemo(() => translations?.labels || {}, [translations]);
  const { translations, formTranslations, formLanguage, language } =
    useSelector((state) => state.language);
  const activeTranslations = formTranslations || translations;
  const t = useMemo(
    () => activeTranslations?.labels || {},
    [activeTranslations],
  );

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";

  // Redux master data
  const {
    occupationOptions: reduxOccupationOptions,
    religionOptions: reduxReligionOptions,
    categoryOptions: reduxCategoryOptions,
    fatherStateOptions: reduxFatherStateOptions,
    classOptions: reduxClassOptions,
  } = useSelector((state) => state.masterData);

  // Local state
  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA });
  const [isSeatAllocate, setIsSeatAllocate] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // Group options from API
  const [preferredGroupOptions, setPreferredGroupOptions] = useState([]);
  // Section options for Change Section
  const [sectionOptions, setSectionOptions] = useState([]);
  // Store full group details
  const [groupDetails, setGroupDetails] = useState([]);

  // Tamil keyboard
  const [showTamilKeyboard, setShowTamilKeyboard] = useState(false);
  const [activeInputName, setActiveInputName] = useState("");

  // Draggable keyboard
  const [kbPosition, setKbPosition] = useState({ x: 100, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);

  // ---- Deferred dropdown normalization refs ----
  const rawApiDataRef = useRef(null);
  const normalizedOnceRef = useRef(false);

  // ==================== DRAG HANDLERS ====================
  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragOffset.current = {
        x: clientX - kbPosition.x,
        y: clientY - kbPosition.y,
      };
      setIsDragging(true);
    },
    [kbPosition],
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setKbPosition({
        x: Math.max(
          0,
          Math.min(clientX - dragOffset.current.x, window.innerWidth - 50),
        ),
        y: Math.max(
          0,
          Math.min(clientY - dragOffset.current.y, window.innerHeight - 50),
        ),
      });
    },
    [isDragging],
  );

  // ===== Derived: should we show the Assigned Class/Section dropdowns? =====
  const showSeatAllocationFields =
    formData.current_status === "Proceed" && isSeatAllocate === 1;

  // ==================== STATUS DROPDOWN HELPERS ====================
  const getStatusOptions = (currentStatus) => {
    const baseOptions = [{ value: "", label: t?.select || "Select" }];

    if (!currentStatus || currentStatus === "") {
      return baseOptions;
    }

    const statusMap = {
      Preview: [
        { value: "Submitted", label: "Submitted" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Submitted: [
        { value: "Proceed", label: t?.proceed || "Proceed" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Proceed:
        isSeatAllocate === 1
          ? [
              {
                value: "Seat Allocated",
                label: t?.seatAllocated || "Seat Allocated",
              },
              { value: "Rejected", label: t?.reject || "Rejected" },
            ]
          : [{ value: "Rejected", label: t?.reject || "Rejected" }],
      "Seat Allocated": [],
      "Invite Not Sent": [],
      "Interview Scheduled": [
        { value: "Approved", label: t?.approved || "Approved" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Approved: [{ value: "Rejected", label: t?.reject || "Rejected" }],
      Rejected: [{ value: "Proceed", label: t?.proceed || "Proceed" }],
    };

    const options = statusMap[currentStatus] || [];

    return [...baseOptions, ...options];
  };

  const shouldDisableStatus = (currentStatus) => {
    if (!currentStatus || currentStatus === "") {
      return true;
    }

    const disabledStatuses = ["Seat Allocated", "Invite Not Sent", "Admitted"];

    if (disabledStatuses.includes(currentStatus)) {
      return true;
    }

    const validStatuses = [
      "Submitted",
      "Proceed",
      "Interview Scheduled",
      "Approved",
      "Preview",
      "Rejected",
    ];

    return !validStatuses.includes(currentStatus);
  };
  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("touchmove", handleDragMove, { passive: false });
    window.addEventListener("touchend", handleDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ==================== DROPDOWN OPTIONS (memoized) ====================
  const genderOptions = useMemo(
    () =>
      GENDER_OPTIONS.map((opt) => ({
        ...opt,
        label: t?.[opt.label?.toLowerCase()] || opt.label,
      })),
    [t],
  );

  const religionOptions = useMemo(
    () => [
      { value: "", label: t?.selectReligion || "Select Religion" },
      ...reduxReligionOptions,
    ],
    [reduxReligionOptions, t],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "", label: t?.selectCategory || "Select Category" },
      ...reduxCategoryOptions,
    ],
    [reduxCategoryOptions, t],
  );

  const stateOptions = useMemo(
    () => [
      { value: "", label: t?.selectState || "Select State" },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions, t],
  );

  const occupationOptions = useMemo(
    () => [
      { value: "", label: t?.selectOccupation || "Select Occupation" },
      ...reduxOccupationOptions,
    ],
    [reduxOccupationOptions, t],
  );

  const classOptions = useMemo(() => {
    const filteredClasses = reduxClassOptions.filter(
      (item) => item.label === "X" || item.label === "XI",
    );

    return [
      { value: "", label: t?.selectClass || "Select Class" },
      ...filteredClasses,
    ];
  }, [reduxClassOptions, t]);

  const livingWithOptions = useMemo(
    () =>
      LIVING_WITH_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.select || "Select"
            : opt.value === "parent"
              ? t?.withParents || "With Parents"
              : t?.withGuardian || "With Guardian",
      })),
    [t],
  );

  const motherTongueOptions = useMemo(
    () =>
      MOTHER_TONGUE_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.selectMotherTongue || "Select Mother Tongue"
            : opt.label,
      })),
    [t],
  );

  const mediumOptions = useMemo(
    () =>
      MEDIUM_OPTIONS.map((opt) => ({
        ...opt,
        label: opt.value === "" ? t?.select || "Select" : opt.label,
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.selectStatus || "Select Status"
            : t?.[opt.value.toLowerCase()] || opt.label,
      })),
    [t],
  );
  // ==================== FETCH HS GROUPS AND SECTIONS ====================
  const fetchHSGroupsAndSections = useCallback(
    async (className) => {
      if (!className) {
        setPreferredGroupOptions([]);
        setGroupDetails([]);
        setSectionOptions([]);
        return;
      }

      try {
        // Convert "First Year" to "XI" and "Second Year" to "XII"
        const classValue = className === "First Year" ? "XI" : "XII";

        const response = await AxiosInstance.post(
          "/admission/hsgroupssections",
          {
            academic_id: academic_year_id,
            school_id: school_id,
            class: classValue,
          },
        );

        if (response.data?.status && Array.isArray(response.data.data)) {
          const groups = response.data.data;
          setGroupDetails(groups);

          // Update preferred group options
          const groupOptions = [
            { value: "", label: t?.select || "Select" },
            ...groups.map((group) => ({
              value: group.group_name,
              label: group.name,
              medium: group.medium,
              group_name: group.group_name,
            })),
          ];
          setPreferredGroupOptions(groupOptions);

          // Update section options
          const sectionOpts = [
            { value: "", label: t?.select || "Select", originalData: null },
            ...groups.map((item) => ({
              value: item.section_id,
              label: item.name,
              originalData: item,
            })),
          ];
          setSectionOptions(sectionOpts);
        } else {
          setPreferredGroupOptions([
            { value: "", label: t?.select || "Select" },
          ]);
          setGroupDetails([]);
          setSectionOptions([]);
        }
      } catch (error) {
        console.error("❌ Fetch HS groups and sections error:", error);
        setPreferredGroupOptions([{ value: "", label: t?.select || "Select" }]);
        setGroupDetails([]);
        setSectionOptions([]);
      }
    },
    [academic_year_id, school_id, t],
  );
  // ==================== FETCH MASTER DATA ====================
  const fetchAllMasterData = useCallback(() => {
    dispatch(fetchReligionOptions());
    dispatch(fetchCategoryOptions());
    dispatch(fetchOccupationOptions());
    dispatch(fetchClassOptions({ id: school_id, academic_year }));
    dispatch(fetchCountryOptions()).then((action) => {
      if (action.payload && Array.isArray(action.payload)) {
        const india = action.payload.find(
          (c) => c.label?.toLowerCase() === "india",
        );
        if (india)
          dispatch(
            fetchStateOptions({
              countryId: india.value,
              stateType: STATE_TYPES.FATHER,
            }),
          );
      }
    });
  }, [dispatch, school_id, academic_year]);

  // ==================== FETCH ADMISSION DATA ====================
  const fetchAdmissionData = useCallback(async () => {
    if (!admissionId) return;
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/admission/viewHigherSecondryAdmission",
        { id: admissionId },
      );
      if (response.data?.status && response.data?.data) {
        const d = response.data.data;
        const fmt = (val) => (val ? dayjs(val).format("YYYY-MM-DD") : null);

        // ── Switch language based on form_language ──
        const formLang = d?.form_language || "en";
        // dispatch(switchLanguage(formLang));
        dispatch(switchFormLanguage(formLang));
        // localStorage.setItem("appLanguage", formLang);

        // Calculate totals
        const marks = {
          tamil_mark: d.tamil_mark != null ? String(d.tamil_mark) : "",
          english_mark: d.english_mark != null ? String(d.english_mark) : "",
          mathematics_mark:
            d.mathematics_mark != null ? String(d.mathematics_mark) : "",
          science_mark: d.science_mark != null ? String(d.science_mark) : "",
          social_science_mark:
            d.social_science_mark != null ? String(d.social_science_mark) : "",
        };

        const markValues = MARK_FIELDS.map((f) => parseInt(marks[f]) || 0);
        const totalMark = markValues.reduce((a, b) => a + b, 0);
        const totalPct = parseFloat(((totalMark / 500) * 100).toFixed(2));

        // ── Store raw values for deferred dropdown normalization ──
        rawApiDataRef.current = {
          gender: d.gender || "",
          religion: d.religion || "",
          community: d.community || "",
          student_native_state: d.student_native_state || "",
          parent_occupation: d.parent_occupation || "",
          parent_state: d.parent_state || "",
          guardian_occupation: d.guardian_occupation || "",
          guardian_state: d.guardian_state || "",
          last_studied_class: d.last_studied_class || "",
          // Static dropdown raw values
          living_with: d.living_with || "",
          qualified_eligibility_exam: d.qualified_eligibility_exam || "",
          exam_board_name: d.exam_board_name || "",
          qualified_exam_name: d.qualified_exam_name || "",
          preferred_study_course: d.preferred_study_course || "",
          admission_for_which_year: d.admission_for_which_year || "",
          mother_tongue: d.mother_tongue || "",
          last_medium_of_instruction: d.last_medium_of_instruction || "",
          // Radio button raw values
          willing_to_join_services:
            d.willing_to_join_services != null
              ? String(d.willing_to_join_services)
              : "",
          is_vaccinated_pox:
            d.is_vaccinated_pox != null ? String(d.is_vaccinated_pox) : "",
        };
        normalizedOnceRef.current = false;
        // ── Set form data — dropdown/radio fields set to "" initially ──
        // They will be resolved in the deferred normalization useEffect
        setIsSeatAllocate(d.is_seatAllocate || 0);
        const newFormData = {
          date: fmt(d.date) || moment().format("YYYY-MM-DD"),
          applicant_name: d.applicant_name || "",
          applicant_name_in_tamil: d.applicant_name_in_tamil || "",
          emis_id: d.emis_id || "",
          aadhaar_no: d.aadhaar_no != null ? String(d.aadhaar_no) : "",
          email: d.email || "",
          mobile_number: d.mobile_number || "",
          student_image: d.student_image || "",
          student_image_original_name:
            d.student_original_path || d.student_image || "",
          // Dropdown fields — set "" initially, deferred normalization will resolve
          gender: "",
          date_of_birth: fmt(d.date_of_birth),
          religion: "",
          caste: d.caste || "",
          community: "",
          nationality: d.nationality || "Indian",
          student_native_state: "",
          place_of_birth: d.place_of_birth || "",
          community_certificate_path: d.community_certificate_path || "",
          community_certificate_original_name:
            d.community_original_path || d.community_certificate_path || "",
          // Parent — text fields keep raw, dropdown fields ""
          living_with: "",
          parent_name: d.parent_name || "",
          parent_occupation: "",
          parent_annual_salary:
            d.parent_annual_salary != null
              ? String(d.parent_annual_salary)
              : "",
          parent_address: d.parent_address || "",
          parent_city: d.parent_city || "",
          parent_district: d.parent_district || "",
          parent_state: "",
          parent_pincode:
            d.parent_pincode != null ? String(d.parent_pincode) : "",
          // Guardian — text fields keep raw, dropdown fields ""
          guardian_name: d.guardian_name || "",
          guardian_occupation: "",
          guardian_annual_salary:
            d.guardian_annual_salary != null
              ? String(d.guardian_annual_salary)
              : "",
          guardian_address: d.guardian_address || "",
          guardian_city: d.guardian_city || "",
          guardian_district: d.guardian_district || "",
          guardian_state: "",
          guardian_pincode:
            d.guardian_pincode != null ? String(d.guardian_pincode) : "",
          // Academic — set "" initially for dropdowns
          qualified_eligibility_exam: "",
          exam_board_name: "",
          // Public Exam
          qualified_exam_name: "",
          exam_month_and_year: d.exam_month_and_year || null,
          register_number:
            d.register_number != null ? String(d.register_number) : "",
          ...marks,
          total_mark: d.total_mark || totalMark,
          total_percentage: d.total_percentage
            ? parseFloat(d.total_percentage)
            : totalPct,
          // Preferred Course — set "" initially for dropdowns
          preferred_study_course: "",
          admission_for_which_year: "",
          preferred_group_1: d.preferred_group_1 || "",
          preferred_group_2: d.preferred_group_2 || "",
          preferred_group_3: d.preferred_group_3 || "",
          preferred_group_1_medium: d.preferred_group_1_medium || null,
          preferred_group_2_medium: d.preferred_group_2_medium || null,
          preferred_group_3_medium: d.preferred_group_3_medium || null,
          mother_tongue: "",
          last_medium_of_instruction: "",
          // Last Studied — class dropdown ""
          last_studied_class: "",
          studied_year: d.studied_year || null,
          last_studied_school_name: d.last_studied_school_name || "",
          reason_for_discontinuing: d.reason_for_discontinuing || "",
          // Other — radio buttons ""
          willing_to_join_services: "",
          is_vaccinated_pox: "",
          certificate_path: d.certificate_path || "",
          certificate_original_name:
            d.certificate_original_path || d.certificate_path || "",
          place: d.place || "",
          // Status
          assigned_class: d.assigned_class || "",
          assigned_group: d.assigned_group_name || "",
          assigned_section: d.assigned_section || "",
          change_section: d.change_section || "",
          current_status: d.status || "",
          assigned_group_id: d.assigned_group_id,
          assigned_medium: d.assigned_medium,
          status: "",
          feedback: "",
          ocr_upload_path: d.ocr_upload_path || null,
        };

        setFormData(newFormData);
      }
    } catch (error) {
      console.error("View admission error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details.",
      });
    } finally {
      setLoading(false);
    }
  }, [admissionId, t, dispatch]);

  // ==================== DEFERRED DROPDOWN NORMALIZATION ====================
  useEffect(() => {
    if (!rawApiDataRef.current || normalizedOnceRef.current) return;
    // Wait until at least some redux options have loaded
    const hasOptions =
      reduxReligionOptions.length > 0 ||
      reduxCategoryOptions.length > 0 ||
      reduxFatherStateOptions.length > 0 ||
      reduxClassOptions.length > 0;
    if (!hasOptions) return;

    normalizedOnceRef.current = true;
    const raw = rawApiDataRef.current;

    setFormData((prev) => {
      // ── Normalize Redux-dependent dropdown fields ──
      const normalizedGender = findMatchInOptions(raw.gender, genderOptions);
      const normalizedReligion = findMatchInOptions(
        raw.religion,
        religionOptions,
      );
      const normalizedCommunity = findMatchInOptions(
        raw.community,
        categoryOptions,
      );
      const normalizedStudentNativeState = findMatchInOptions(
        raw.student_native_state,
        stateOptions,
      );
      const normalizedParentOccupation = findMatchInOptions(
        raw.parent_occupation,
        occupationOptions,
        "label",
      );
      const normalizedParentState = findMatchInOptions(
        raw.parent_state,
        stateOptions,
      );
      const normalizedGuardianOccupation = findMatchInOptions(
        raw.guardian_occupation,
        occupationOptions,
        "label",
      );
      const normalizedGuardianState = findMatchInOptions(
        raw.guardian_state,
        stateOptions,
      );
      const normalizedLastStudiedClass = findMatchInOptions(
        raw.last_studied_class,
        classOptions,
        "label",
      );

      // ── Normalize static dropdown fields ──
      const normalizedLivingWith = normalizeValueAgainstOptions(
        raw.living_with,
        LIVING_WITH_OPTIONS,
      );
      const normalizedQualifiedExam = normalizeValueAgainstOptions(
        raw.qualified_eligibility_exam,
        QUALIFYING_EXAM_OPTIONS,
      );
      const normalizedExamBoard = normalizeValueAgainstOptions(
        raw.exam_board_name,
        EXAM_BOARD_OPTIONS,
      );
      const normalizedQualifiedExamName = normalizeValueAgainstOptions(
        raw.qualified_exam_name,
        QUALIFYING_EXAM_OPTIONS,
      );
      const normalizedPreferredCourse = normalizeValueAgainstOptions(
        raw.preferred_study_course,
        COURSE_PREFERRED_OPTIONS,
      );
      const normalizedAdmissionYear = normalizeValueAgainstOptions(
        raw.admission_for_which_year,
        YEAR_OF_ADMISSION_OPTIONS,
      );
      const normalizedMotherTongue = findMatchInOptions(
        raw.mother_tongue,
        MOTHER_TONGUE_OPTIONS,
      );
      const normalizedLastMedium = normalizeValueAgainstOptions(
        raw.last_medium_of_instruction,
        MEDIUM_OPTIONS,
      );

      // ── Normalize radio button fields ──
      const normalizedWillingToJoin = normalizeValueAgainstOptions(
        raw.willing_to_join_services,
        YES_NO_OPTIONS,
      );
      const normalizedVaccinated = normalizeValueAgainstOptions(
        raw.is_vaccinated_pox,
        YES_NO_OPTIONS,
      );

      return {
        ...prev,
        // Redux-dependent dropdowns
        gender: normalizedGender,
        religion: normalizedReligion,
        community: normalizedCommunity,
        student_native_state: normalizedStudentNativeState,
        parent_occupation: normalizedParentOccupation,
        parent_state: normalizedParentState,
        guardian_occupation: normalizedGuardianOccupation,
        guardian_state: normalizedGuardianState,
        last_studied_class: normalizedLastStudiedClass,
        // Static dropdowns
        living_with: normalizedLivingWith,
        qualified_eligibility_exam: normalizedQualifiedExam,
        exam_board_name: normalizedExamBoard,
        qualified_exam_name: normalizedQualifiedExamName,
        preferred_study_course: normalizedPreferredCourse,
        admission_for_which_year: normalizedAdmissionYear,
        mother_tongue: normalizedMotherTongue,
        last_medium_of_instruction: normalizedLastMedium,
        // Radio buttons (with sensible defaults)
        willing_to_join_services: normalizedWillingToJoin || "No",
        is_vaccinated_pox: normalizedVaccinated || "Yes",
      };
    });
  }, [
    reduxReligionOptions,
    reduxCategoryOptions,
    reduxFatherStateOptions,
    reduxOccupationOptions,
    reduxClassOptions,
    genderOptions,
    religionOptions,
    categoryOptions,
    stateOptions,
    occupationOptions,
    classOptions,
  ]);

  // ==================== NORMALIZE PREFERRED GROUPS WHEN GROUP OPTIONS LOAD ====================
  useEffect(() => {
    setFormData((prev) => {
      const normalizedGroup1 =
        prev.preferred_group_1 &&
        preferredGroupOptions.some((e) => e.value === prev.preferred_group_1)
          ? prev.preferred_group_1
          : "";

      const normalizedGroup2 =
        prev.preferred_group_2 &&
        preferredGroupOptions.some((e) => e.value === prev.preferred_group_2)
          ? prev.preferred_group_2
          : "";

      const normalizedGroup3 =
        prev.preferred_group_3 &&
        preferredGroupOptions.some((e) => e.value === prev.preferred_group_3)
          ? prev.preferred_group_3
          : "";

      return {
        ...prev,
        preferred_group_1: normalizedGroup1,
        preferred_group_2: normalizedGroup2,
        preferred_group_3: normalizedGroup3,
      };
    });
  }, [preferredGroupOptions]);

  // ==================== ON OPEN ====================
  useEffect(() => {
    if (open && admissionId) {
      const fetchAllData = async () => {
        await Promise.all([fetchAllMasterData(), fetchAdmissionData()]);
      };

      fetchAllData();
    }

    if (!open) {
      setFormData({ ...INITIAL_FORM_DATA });
      setErrors({});
      setShowTamilKeyboard(false);
      setPreferredGroupOptions([]);
      setGroupDetails([]);
      setSectionOptions([]);
      rawApiDataRef.current = null;
      normalizedOnceRef.current = false;
      dispatch(resetFormLanguage());
    }
  }, [open, admissionId, fetchAllMasterData, fetchAdmissionData]);
  // ==================== FETCH GROUPS AND SECTIONS WHEN YEAR CHANGES ====================
  useEffect(() => {
    if (formData.admission_for_which_year) {
      fetchHSGroupsAndSections(formData.admission_for_which_year);
    }
  }, [formData.admission_for_which_year, fetchHSGroupsAndSections]);

  // ==================== RECALCULATE MARKS ====================
  useEffect(() => {
    const markValues = MARK_FIELDS.map((f) => parseInt(formData[f]) || 0);
    const total = markValues.reduce((a, b) => a + b, 0);
    const pct = parseFloat(((total / 500) * 100).toFixed(2));
    if (formData.total_mark !== total || formData.total_percentage !== pct) {
      setFormData((prev) => ({
        ...prev,
        total_mark: total,
        total_percentage: pct,
      }));
    }
  }, [
    formData.tamil_mark,
    formData.english_mark,
    formData.mathematics_mark,
    formData.science_mark,
    formData.social_science_mark,
  ]);

  // ==================== HANDLERS ====================
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (
        NUMBER_ONLY_FIELDS.includes(name) &&
        value !== "" &&
        !/^\d*$/.test(value)
      )
        return;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      // Handle status changes — clear feedback when status is not Rejected/Approved
      if (name === "status") {
        const newStatusVal = value?.value !== undefined ? value.value : value;
        setFormData((prev) => ({
          ...prev,
          status: newStatusVal,
          feedback:
            newStatusVal === "Rejected" || newStatusVal === "Approved"
              ? prev.feedback
              : "",
        }));
        if (errors.status) setErrors((prev) => ({ ...prev, status: "" }));
        if (errors.feedback) setErrors((prev) => ({ ...prev, feedback: "" }));
        return;
      }

      // Handle preferred group fields specially
      if (name.startsWith("preferred_group_")) {
        if (
          typeof value === "object" &&
          value !== null &&
          value.value !== undefined
        ) {
          setFormData((prev) => ({ ...prev, [name]: value.value }));
        } else {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else {
        const finalValue = value?.value !== undefined ? value.value : value;
        setFormData((prev) => ({ ...prev, [name]: finalValue }));
      }

      // Clear group selections when year changes and fetch new groups/sections
      if (name === "admission_for_which_year") {
        setFormData((prev) => ({
          ...prev,
          preferred_group_1: "",
          preferred_group_2: "",
          preferred_group_3: "",
          change_section: "",
        }));
      }

      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleRadioChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDateChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleMonthYearChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM") : null;
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  // Tamil keyboard handlers
  const handleTamilInputFocus = (name) => {
    if (!NUMBER_ONLY_FIELDS.includes(name)) setActiveInputName(name);
  };

  const handleTamilKeyPress = (char) => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + char,
    }));
    if (errors[activeInputName])
      setErrors((prev) => ({ ...prev, [activeInputName]: "" }));
  };

  const handleTamilBackspace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "").slice(0, -1),
    }));
  };

  const handleTamilSpace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + " ",
    }));
  };

  // ==================== FILE UPLOAD ====================
  const handleFileUpload = useCallback(
    async (file, fieldKey, uploadKey, allowedTypes) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          [`${fieldKey}_original_name`]: "",
          [fieldKey]: "",
        }));
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [fieldKey]: t?.allowedPdfJpgPng || "Invalid file type",
        }));
        return;
      }
      try {
        const payload = new FormData();
        payload.append(uploadKey, file);
        if (school_id) {
          payload.append("school_id", school_id);
        }
        const response = await AxiosInstance.post(
          "/groupCockpit/upload",
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploaded = response.data[0];

          if (fieldKey === "community_certificate_path") {
            setFormData((prev) => ({
              ...prev,
              [`community_certificate_original_name`]: uploaded.original_name,
              [fieldKey]: uploaded.upload_path,
            }));
            setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          } else if (fieldKey === "certificate_path") {
            setFormData((prev) => ({
              ...prev,
              [`certificate_original_name`]: uploaded.original_name,
              [fieldKey]: uploaded.upload_path,
            }));
            setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          } else {
            setFormData((prev) => ({
              ...prev,
              [`student_image_original_name`]: uploaded.original_name,
              [fieldKey]: uploaded.upload_path,
            }));
            setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          }
        }
      } catch (error) {
        console.error(`${fieldKey} upload error:`, error);
        setErrors((prev) => ({
          ...prev,
          [fieldKey]: t?.fileUploadFailed || "Upload failed.",
        }));
      }
    },
    [t],
  );

  const handleCommunityFileChange = useCallback(
    (file) => {
      handleFileUpload(
        file,
        "community_certificate_path",
        "community_certificate",
        ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      );
    },
    [handleFileUpload],
  );

  const handleCoCurricularFileChange = useCallback(
    (file) => {
      handleFileUpload(file, "certificate_path", "co-curricular_certificate", [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ]);
    },
    [handleFileUpload],
  );

  const handleStudentImageChange = useCallback(
    (file) => {
      handleFileUpload(file, "student_image", "admission_student_image", [
        "image/jpeg",
        "image/jpg",
        "image/png",
      ]);
    },
    [handleFileUpload],
  );

  // ==================== RESIDENTIAL HELPERS ====================
  const isGuardian = (formData.living_with || "").toLowerCase() === "guardian";

  const getResField = (parentField, guardianField) =>
    isGuardian ? guardianField : parentField;

  const getResValue = (parentField, guardianField) =>
    isGuardian ? formData[guardianField] : formData[parentField];

  const getResError = (parentField, guardianField) =>
    isGuardian ? errors[guardianField] : errors[parentField];

  // ==================== HELPERS ====================
  const getVal = (field) => {
    if (!field && field !== 0) return null;
    return typeof field === "object" && field.value !== undefined
      ? field.value
      : field;
  };

  const getLabel = (field) => {
    if (!field && field !== 0) return null;
    return typeof field === "object" && field.label !== undefined
      ? field.label
      : field;
  };

  const handleUpdateChanges = async () => {
    // ── Validate before submitting ──
    const validationErrors = validateHSEditForm(formData, translations);
    console.log("validationErrors", validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error field
      const firstErrorKey = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const livingWith = getVal(formData.living_with);
    const isParent = livingWith === "parent";
    const isGuard = livingWith === "guardian";

    try {
      setSubmitting(true);

      // Find the selected section option using the stored change_section value
      const selectedOption = sectionOptions.find(
        (opt) => opt.value === formData.change_section,
      );

      // Extract values from selected section's originalData
      let assigned_section = null;
      let assigned_group_name = null;
      let assigned_group_id = null;
      let assigned_medium = null;
      let assigned_class = null;

      if (selectedOption && selectedOption.originalData) {
        const sectionData = selectedOption.originalData;
        assigned_section = sectionData.section_id;
        assigned_group_name = sectionData.group_name;
        assigned_group_id = sectionData.id;
        assigned_medium = sectionData.medium;
        assigned_class = sectionData.domain_id;
      }

      const payload = {
        id: admissionId,
        aadhaar_no: formData.aadhaar_no,
        applicant_name: getVal(formData.applicant_name),
        applicant_name_in_tamil: getVal(formData.applicant_name_in_tamil),
        emis_id: getVal(formData.emis_id) || null,
        email: getVal(formData.email),
        mobile_number: getVal(formData.mobile_number),
        student_image: getVal(formData.student_image) || null,
        student_original_path: formData.student_image_original_name || null,
        date_of_birth: getVal(formData.date_of_birth),
        gender: getLabel(formData.gender),
        nationality: getVal(formData.nationality),
        student_native_state: getLabel(formData.student_native_state),
        place_of_birth: getVal(formData.place_of_birth),
        religion: getLabel(formData.religion),
        caste: getVal(formData.caste),
        community: getLabel(formData.community),
        community_certificate_path:
          getVal(formData.community_certificate_path) || null,
        community_original_path:
          formData.community_certificate_original_name || null,
        living_with: livingWith,
        parent_name: isParent ? getVal(formData.parent_name) : null,
        parent_address: isParent ? getVal(formData.parent_address) : null,
        parent_city: isParent ? getVal(formData.parent_city) : null,
        parent_district: isParent ? getVal(formData.parent_district) : null,
        parent_state: isParent ? getLabel(formData.parent_state) : null,
        parent_pincode: isParent ? getVal(formData.parent_pincode) : null,
        parent_occupation: isParent
          ? getLabel(formData.parent_occupation)
          : null,
        parent_annual_salary: isParent
          ? parseInt(formData.parent_annual_salary) || 0
          : null,
        guardian_name: isGuard ? getVal(formData.guardian_name) : null,
        guardian_address: isGuard ? getVal(formData.guardian_address) : null,
        guardian_city: isGuard ? getVal(formData.guardian_city) : null,
        guardian_district: isGuard ? getVal(formData.guardian_district) : null,
        guardian_state: isGuard ? getLabel(formData.guardian_state) : null,
        guardian_pincode: isGuard ? getVal(formData.guardian_pincode) : null,
        guardian_occupation: isGuard
          ? getLabel(formData.guardian_occupation)
          : null,
        guardian_annual_salary: isGuard
          ? parseInt(formData.guardian_annual_salary) || 0
          : null,
        qualified_eligibility_exam: getVal(formData.qualified_eligibility_exam),
        exam_board_name: getVal(formData.exam_board_name),
        qualified_exam_name: getVal(formData.qualified_exam_name),
        exam_month_and_year: getVal(formData.exam_month_and_year),
        register_number: getVal(formData.register_number),
        tamil_mark: parseInt(formData.tamil_mark) || 0,
        english_mark: parseInt(formData.english_mark) || 0,
        mathematics_mark: parseInt(formData.mathematics_mark) || 0,
        science_mark: parseInt(formData.science_mark) || 0,
        social_science_mark: parseInt(formData.social_science_mark) || 0,
        total_mark: formData.total_mark,
        total_percentage: formData.total_percentage,
        place: getVal(formData.place),
        preferred_study_course: getVal(formData.preferred_study_course),
        preferred_group_1: formData.preferred_group_1 || null,
        preferred_group_2: formData.preferred_group_2 || null,
        preferred_group_3: formData.preferred_group_3 || null,

        // Add mediums
        preferred_group_1_medium: formData.preferred_group_1
          ? preferredGroupOptions.find(
              (g) => g.value === formData.preferred_group_1,
            )?.medium || null
          : null,
        preferred_group_2_medium: formData.preferred_group_2
          ? preferredGroupOptions.find(
              (g) => g.value === formData.preferred_group_2,
            )?.medium || null
          : null,
        preferred_group_3_medium: formData.preferred_group_3
          ? preferredGroupOptions.find(
              (g) => g.value === formData.preferred_group_3,
            )?.medium || null
          : null,
        last_studied_class: getVal(formData.last_studied_class),
        studied_year: getVal(formData.studied_year),
        last_studied_school_name: getVal(formData.last_studied_school_name),
        reason_for_discontinuing: getVal(formData.reason_for_discontinuing),
        admission_for_which_year: getVal(formData.admission_for_which_year),
        mother_tongue: getVal(formData.mother_tongue),
        last_medium_of_instruction: getVal(formData.last_medium_of_instruction),
        willing_to_join_services: getVal(formData.willing_to_join_services),
        certificate_path: getVal(formData.certificate_path) || null,
        certificate_original_path: formData.certificate_original_name || null,
        is_vaccinated_pox: getVal(formData.is_vaccinated_pox),
        updated_by: user_id || 1,
        // Change section details
        assigned_section: assigned_section
          ? assigned_section
          : formData?.assigned_section,
        assigned_group_name: assigned_group_name
          ? assigned_group_name
          : formData?.assigned_group,
        assigned_group_id: assigned_group_id
          ? assigned_group_id
          : formData?.assigned_group_id,
        assigned_medium: assigned_medium
          ? assigned_medium
          : formData?.assigned_medium,
        assigned_class: assigned_class
          ? assigned_class
          : formData?.assigned_class,
        // Status
        status: formData?.status ? formData?.status : formData?.current_status,
        feedback: getVal(formData.feedback) || null,
        form_language: formLanguage || language,
      };

      const response = await AxiosInstance.put(
        "/admission/updateHigherSecondryAdmission",
        payload,
      );

      if (response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: t?.updateSuccess || "Updated successfully!",
        });
        setTimeout(() => {
          setModalState({ open: false, type: "", message: "" });
          onClose();
          if (fetchAdmissionList) fetchAdmissionList();
        }, 1500);
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message || t?.updateFailed || "Update failed.",
        });
      }
    } catch (error) {
      console.error("❌ Update error:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error.response?.data?.message || t?.updateFailed || "Update failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ ...INITIAL_FORM_DATA });
    setErrors({});
    setShowTamilKeyboard(false);
    rawApiDataRef.current = null;
    normalizedOnceRef.current = false;
    dispatch(resetFormLanguage());
    onClose();
  };

  // ==================== SECTION HEADER COMPONENT ====================
  const SectionHeader = ({ title }) => (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionIcon}>
        <FaClipboardList />
      </div>
      <h2 className={styles.sectionTitle}>{title}</h2>
    </div>
  );

  // OCR PDF preview — blob paths (Upload/OfflineApplications/...) are
  // fetched through /content/image into an object URL; legacy paths
  // keep the static URL.
  const [ocrPreviewUrl, setOcrPreviewUrl] = useState("");
  useEffect(() => {
    const p = formData.ocr_upload_path || "";
    if (!p) {
      setOcrPreviewUrl("");
      return undefined;
    }
    const isBlobPath =
      !/^https?:\/\//i.test(p) && !p.startsWith("/") && p.includes("/Upload/");
    if (!isBlobPath) {
      setOcrPreviewUrl(/^https?:\/\//i.test(p) ? p : `${IMAGE_BASE_URL}${p}`);
      return undefined;
    }
    let cancelled = false;
    AxiosInstance.get("/content/image", {
      params: { path: p },
      responseType: "blob",
    })
      .then((res) => {
        if (cancelled) return;
        setOcrPreviewUrl(
          URL.createObjectURL(
            new Blob([res.data], {
              type: res.headers["content-type"] || "application/pdf",
            }),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setOcrPreviewUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [formData.ocr_upload_path]);

  // Blob-aware preview: raw stored path in — blob paths fetched through
  // the authenticated /content/image endpoint into an object URL,
  // legacy /uploads paths keep the old static URL.
  const handlePreview = async (path) => {
    if (!path) return;
    const p = String(path);
    const isBlobPath =
      !/^https?:\/\//i.test(p) && !p.startsWith("/") && p.includes("/Upload/");
    if (isBlobPath) {
      try {
        const res = await AxiosInstance.get("/content/image", {
          params: { path: p },
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(
          new Blob([res.data], {
            type: res.headers["content-type"] || "application/octet-stream",
          }),
        );
        window.open(objectUrl, "_blank");
      } catch (err) {
        console.error("Preview error:", err);
      }
      return;
    }
    window.open(/^https?:\/\//i.test(p) ? p : `${IMAGE_BASE_URL}${p}`, "_blank");
  };

  // ==================== RENDER ====================
  return (
    <div className={styles.modalDialog}>
      <Modal
        show={open}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
        scrollable
        dialogClassName={styles.modalDialog}
      >
        {/* ── HEADER ── */}
        <Modal.Header closeButton className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <Modal.Title className={styles.modalTitle}>
              {t?.higherSecondaryApplication || "Higher Secondary Application"}{" "}
              {rowData?.application_no ? `- ${rowData.application_no}` : ""}
            </Modal.Title>
            <button
              type="button"
              className={`${styles.tamilKeyboardToggle} ${showTamilKeyboard ? styles.tamilKeyboardToggleActive : ""}`}
              onClick={() => setShowTamilKeyboard((prev) => !prev)}
            >
              {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
            </button>
          </div>
        </Modal.Header>

        {/* ── BODY ── */}
        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingContainer}>
              {t?.loading || "Loading..."}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: formData.ocr_upload_path ? "20px" : "0",
                }}
              >
                {formData.ocr_upload_path && (
                  <div className={styles.previewPane}>
                    <iframe
                      src={ocrPreviewUrl}
                      title="PDF Preview"
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                    />
                  </div>
                )}
                <div
                  className={styles.formPane}
                  style={{
                    width: formData.ocr_upload_path ? undefined : "100%",
                  }}
                >
                  <div className={styles.formContainer}>
                    {/* ══════ TAMIL KEYBOARD ══════ */}
                    {showTamilKeyboard && (
                      <div
                        ref={keyboardRef}
                        className={styles.tamilKeyboard}
                        style={{
                          top: `${kbPosition.y}px`,
                          left: `${kbPosition.x}px`,
                        }}
                      >
                        <div
                          className={styles.tamilKeyboardHeader}
                          onMouseDown={handleDragStart}
                          onTouchStart={handleDragStart}
                          style={{ cursor: isDragging ? "grabbing" : "grab" }}
                        >
                          <div className={styles.tamilKeyboardHeaderLeft}>
                            <FaGripHorizontal
                              size={14}
                              style={{ opacity: 0.6 }}
                            />
                            <h5 className={styles.tamilKeyboardTitle}>
                              {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
                            </h5>
                          </div>
                          <button
                            type="button"
                            className={styles.tamilKeyboardClose}
                            onClick={() => setShowTamilKeyboard(false)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                        <div className={styles.tamilKeyboardBody}>
                          <div className={styles.tamilKeyRow}>
                            {TAMIL_VOWELS.map((char) => (
                              <button
                                key={char}
                                type="button"
                                className={styles.tamilKey}
                                onClick={() => handleTamilKeyPress(char)}
                              >
                                {char}
                              </button>
                            ))}
                          </div>
                          <div className={styles.tamilKeyRow}>
                            {TAMIL_CONSONANTS.map((char) => (
                              <button
                                key={char}
                                type="button"
                                className={styles.tamilKey}
                                onClick={() => handleTamilKeyPress(char)}
                              >
                                {char}
                              </button>
                            ))}
                          </div>
                          <div className={styles.tamilKeyRow}>
                            {TAMIL_MODIFIERS.map((char, idx) => (
                              <button
                                key={idx}
                                type="button"
                                className={styles.tamilKey}
                                onClick={() => handleTamilKeyPress(char)}
                              >
                                {char}
                              </button>
                            ))}
                            <button
                              type="button"
                              className={styles.tamilKeyBackspace}
                              onClick={handleTamilBackspace}
                            >
                              ⌫
                            </button>
                            <button
                              type="button"
                              className={styles.tamilKeySpace}
                              onClick={handleTamilSpace}
                            >
                              ␣
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ══════ BASIC DETAILS ══════ */}
                    <SectionHeader title={t?.basicDetails || "Basic Details"} />
                    <div className="row">
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.date || "Date"}
                          name="date"
                          type="text"
                          value={formData.date}
                          disabled
                          readOnly
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={
                            t?.studentNameEnglish || "Student Name (English)"
                          }
                          name="applicant_name"
                          type="text"
                          value={formData.applicant_name}
                          onChange={handleInputChange}
                          placeholder={
                            t?.studentNameEnglishPlaceholder ||
                            "Enter Student Name in English"
                          }
                          required
                          error={errors.applicant_name}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.studentNameTamil || "Student Name (Tamil)"}
                          name="applicant_name_in_tamil"
                          type="text"
                          value={formData.applicant_name_in_tamil}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus("applicant_name_in_tamil")
                          }
                          placeholder={
                            t?.studentNameTamilPlaceholder ||
                            "Enter Student Name in Tamil"
                          }
                          required
                          error={errors.applicant_name_in_tamil}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.aadharNumber || "Aadhaar No."}
                          name="aadhaar_no"
                          type="text"
                          value={formData.aadhaar_no}
                          onChange={handleInputChange}
                          placeholder={
                            t?.aadharNumberAltPlaceholder || "Ex: 432156789076"
                          }
                          maxLength={12}
                          required
                          error={errors.aadhaar_no}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDatePicker
                          label={t?.dateOfBirth || "Date of Birth"}
                          name="date_of_birth"
                          value={
                            formData.date_of_birth
                              ? dayjs(formData.date_of_birth)
                              : null
                          }
                          onChange={(date) =>
                            handleDateChange("date_of_birth", date)
                          }
                          placeholder={
                            t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"
                          }
                          required
                          error={errors.date_of_birth}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.emailAddress || "Email"}
                          name="email"
                          type="text"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder={
                            t?.emailPlaceholder || "E.g. student@gmail.com"
                          }
                          required
                          error={errors.email}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.mobileNumber || "Mobile No."}
                          name="mobile_number"
                          type="text"
                          value={formData.mobile_number}
                          onChange={handleInputChange}
                          placeholder={
                            t?.mobileNumberPlaceholder || "E.g. Mobile Number"
                          }
                          maxLength={10}
                          required
                          error={errors.mobile_number}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.gender || "Gender"}
                          name="gender"
                          value={formData.gender}
                          onChange={(value) =>
                            handleDropdownChange("gender", value)
                          }
                          options={genderOptions}
                          placeholder={t?.selectGender || "Select Gender"}
                          required
                          error={errors.gender}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.religion || "Religion"}
                          name="religion"
                          value={formData.religion}
                          onChange={(value) =>
                            handleDropdownChange("religion", value)
                          }
                          options={religionOptions}
                          placeholder={t?.selectReligion || "Select Religion"}
                          required
                          error={errors.religion}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.caste || "Caste"}
                          name="caste"
                          type="text"
                          value={formData.caste}
                          onChange={handleInputChange}
                          onFocus={() => handleTamilInputFocus("caste")}
                          placeholder={t?.castePlaceholder || "E.g. Caste"}
                          required
                          error={errors.caste}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.category || "Category"}
                          name="community"
                          value={formData.community}
                          onChange={(value) =>
                            handleDropdownChange("community", value)
                          }
                          options={categoryOptions}
                          placeholder={t?.selectCategory || "Select Category"}
                          required
                          error={errors.community}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.nationality || "Nationality"}
                          name="nationality"
                          type="text"
                          value={formData.nationality}
                          onChange={handleInputChange}
                          placeholder={
                            t?.nationalityPlaceholder || "E.g. Indian"
                          }
                          required
                          error={errors.nationality}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.state || "State"}
                          name="student_native_state"
                          value={formData.student_native_state}
                          onChange={(value) =>
                            handleDropdownChange("student_native_state", value)
                          }
                          options={stateOptions}
                          placeholder={t?.selectState || "Select State"}
                          required
                          error={errors.student_native_state}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.birthPlace || "Birth Place"}
                          name="place_of_birth"
                          type="text"
                          value={formData.place_of_birth}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus("place_of_birth")
                          }
                          placeholder={
                            t?.birthPlacePlaceholder || "E.g. Chennai"
                          }
                          required
                          error={errors.place_of_birth}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomFileInput
                          // label={
                          //   t?.communityCertificate || "Community Certificate"
                          // }
                          label={
                            `${t?.markSheet} (${t?.copy})` ||
                            "Mark Sheet (Copy)"
                          }
                          name="community_certificate_path"
                          value={
                            formData.community_certificate_original_name || null
                          }
                          onChange={handleCommunityFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          placeholder={
                            formData.community_certificate_original_name ||
                            t?.noFileChosen ||
                            "No file chosen"
                          }
                          buttonText={t?.chooseFile || "Choose File"}
                          required
                          error={errors.community_certificate_path}
                        />
                        {/* Preview Button */}
                        {formData?.community_certificate_path && (
                          <button
                            type="button"
                            className={styles.viewBtn}
                            onClick={() =>
                              handlePreview(formData.community_certificate_path)
                            }
                          >
                            <FaEye />
                          </button>
                        )}
                        <span className={styles.fileHint}>
                          {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG."}
                        </span>
                      </div>
                    </div>

                    {/* ══════ RESIDENTIAL DETAILS ══════ */}
                    <SectionHeader
                      title={t?.residentialDetails || "Residential Details"}
                    />
                    <div className="row">
                      <div className="col-lg-12 px-2 col-md-12 col-sm-12">
                        <CustomDropdown
                          label={
                            t?.areYouResidingWith ||
                            "Are you residing with parents or with a guardian?"
                          }
                          name="living_with"
                          value={formData.living_with}
                          onChange={(value) =>
                            handleDropdownChange("living_with", value)
                          }
                          options={livingWithOptions}
                          placeholder={t?.select || "Select"}
                          required
                          error={errors.living_with}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.name || "Name"}
                          name={getResField("parent_name", "guardian_name")}
                          type="text"
                          value={getResValue("parent_name", "guardian_name")}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus(
                              getResField("parent_name", "guardian_name"),
                            )
                          }
                          placeholder={t?.enterHere || "Enter here.."}
                          required
                          error={getResError("parent_name", "guardian_name")}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.occupation || "Occupation"}
                          name={getResField(
                            "parent_occupation",
                            "guardian_occupation",
                          )}
                          value={getResValue(
                            "parent_occupation",
                            "guardian_occupation",
                          )}
                          onChange={(value) =>
                            handleDropdownChange(
                              getResField(
                                "parent_occupation",
                                "guardian_occupation",
                              ),
                              value,
                            )
                          }
                          options={occupationOptions}
                          placeholder={
                            t?.selectOccupation || "Select Occupation"
                          }
                          required
                          error={getResError(
                            "parent_occupation",
                            "guardian_occupation",
                          )}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.annualIncome || "Annual Income"}
                          name={getResField(
                            "parent_annual_salary",
                            "guardian_annual_salary",
                          )}
                          type="text"
                          value={getResValue(
                            "parent_annual_salary",
                            "guardian_annual_salary",
                          )}
                          onChange={handleInputChange}
                          placeholder="e.g., 250000"
                          maxLength={7}
                          required
                          error={getResError(
                            "parent_annual_salary",
                            "guardian_annual_salary",
                          )}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.address || "Address"}
                          name={getResField(
                            "parent_address",
                            "guardian_address",
                          )}
                          type="text"
                          value={getResValue(
                            "parent_address",
                            "guardian_address",
                          )}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus(
                              getResField("parent_address", "guardian_address"),
                            )
                          }
                          placeholder={t?.addressPlaceholder || "E.g. Address"}
                          required
                          error={getResError(
                            "parent_address",
                            "guardian_address",
                          )}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.taluk || "Taluk"}
                          name={getResField("parent_city", "guardian_city")}
                          type="text"
                          value={getResValue("parent_city", "guardian_city")}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus(
                              getResField("parent_city", "guardian_city"),
                            )
                          }
                          placeholder={t?.talukPlaceholder || "E.g. Taluk"}
                          required
                          error={getResError("parent_city", "guardian_city")}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.district || "District"}
                          name={getResField(
                            "parent_district",
                            "guardian_district",
                          )}
                          type="text"
                          value={getResValue(
                            "parent_district",
                            "guardian_district",
                          )}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus(
                              getResField(
                                "parent_district",
                                "guardian_district",
                              ),
                            )
                          }
                          placeholder={
                            t?.districtPlaceholder || "E.g. District"
                          }
                          required
                          error={getResError(
                            "parent_district",
                            "guardian_district",
                          )}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={t?.state || "State"}
                          name={getResField("parent_state", "guardian_state")}
                          value={getResValue("parent_state", "guardian_state")}
                          onChange={(value) =>
                            handleDropdownChange(
                              getResField("parent_state", "guardian_state"),
                              value,
                            )
                          }
                          options={stateOptions}
                          placeholder={t?.selectState || "Select State"}
                          required
                          error={getResError("parent_state", "guardian_state")}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.pincode || "Pincode"}
                          name={getResField(
                            "parent_pincode",
                            "guardian_pincode",
                          )}
                          type="text"
                          value={getResValue(
                            "parent_pincode",
                            "guardian_pincode",
                          )}
                          onChange={handleInputChange}
                          placeholder={
                            t?.pincodePlaceholder || "6-digit pincode"
                          }
                          maxLength={6}
                          required
                          error={getResError(
                            "parent_pincode",
                            "guardian_pincode",
                          )}
                        />
                      </div>
                    </div>

                    {/* ══════ ACADEMIC DETAILS ══════ */}
                    <SectionHeader
                      title={t?.academicDetails || "Academic Details"}
                    />
                    <div className="row">
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={
                            t?.qualifyingExaminationPassed ||
                            "Qualifying Examination Passed"
                          }
                          name="qualified_eligibility_exam"
                          value={formData.qualified_eligibility_exam}
                          onChange={(value) =>
                            handleDropdownChange(
                              "qualified_eligibility_exam",
                              value,
                            )
                          }
                          options={QUALIFYING_EXAM_OPTIONS}
                          placeholder={
                            t?.selectExamination || "Select Examination"
                          }
                          required
                          error={errors.qualified_eligibility_exam}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={
                            t?.nameOfExaminationBoard ||
                            "Name of the Examination Board"
                          }
                          name="exam_board_name"
                          value={formData.exam_board_name}
                          onChange={(value) =>
                            handleDropdownChange("exam_board_name", value)
                          }
                          options={EXAM_BOARD_OPTIONS}
                          placeholder={
                            t?.selectExamination || "Select Examination Board"
                          }
                          required
                          error={errors.exam_board_name}
                        />
                      </div>
                    </div>

                    {/* Instructions Note */}
                    <SectionHeader title={t?.instructions || "Instructions"} />
                    <p
                      style={{
                        padding: "0 0 16px",
                        fontSize: "13px",
                        color: "var(--text-medium-gray, #555)",
                        fontStyle: "italic",
                        lineHeight: 1.6,
                      }}
                    >
                      {t?.publicMarksNote ||
                        "The marks obtained in the qualifying examination must be duly attested either by the Headmaster of the school last studied or by a Gazetted Officer. The original mark certificate should NOT be sent along with the application. Only the attested copy of the mark statement should be enclosed with the application. The original mark statement must be produced at the time of admission."}
                    </p>

                    {/* ══════ PUBLIC EXAMINATION MARKS ══════ */}
                    <SectionHeader
                      title={
                        t?.publicExaminationMarks || "Public Examination Marks"
                      }
                    />
                    <div className="row">
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={
                            t?.nameOfExaminationPassed ||
                            "Name of the Examination Passed"
                          }
                          name="qualified_exam_name"
                          value={formData.qualified_exam_name}
                          onChange={(value) =>
                            handleDropdownChange("qualified_exam_name", value)
                          }
                          options={QUALIFYING_EXAM_OPTIONS}
                          placeholder={
                            t?.selectExamination || "Select Examination"
                          }
                          required
                          error={errors.qualified_exam_name}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={
                            t?.examinationRegisterNumber ||
                            "Examination Register Number"
                          }
                          name="register_number"
                          type="text"
                          value={formData.register_number}
                          onChange={handleInputChange}
                          placeholder={t?.enterHere || "Enter here.."}
                          maxLength={10}
                          required
                          error={errors.register_number}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDatePicker
                          label={
                            t?.monthAndYearOfExamination ||
                            "Month and Year of Examination"
                          }
                          name="exam_month_and_year"
                          value={
                            formData.exam_month_and_year
                              ? dayjs(formData.exam_month_and_year)
                              : null
                          }
                          onChange={(date) =>
                            handleMonthYearChange("exam_month_and_year", date)
                          }
                          placeholder="---------- ----"
                          picker="month"
                          required
                          error={errors.exam_month_and_year}
                        />
                      </div>
                    </div>

                    {/* Marks Table */}
                    <div style={{ overflowX: "auto", padding: "0 0 16px" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "14px",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor:
                                "var(--color-light-grey, #e9ecef)",
                            }}
                          >
                            <th
                              style={{
                                padding: "10px 16px",
                                textAlign: "left",
                                fontWeight: 700,
                                border: "1px solid var(--color-grey, #dee2e6)",
                              }}
                            >
                              {t?.subject || "Subject"}
                            </th>
                            <th
                              style={{
                                padding: "10px 16px",
                                textAlign: "center",
                                fontWeight: 700,
                                border: "1px solid var(--color-grey, #dee2e6)",
                                width: "150px",
                              }}
                            >
                              {t?.maximumMark || "Maximum Marks"}
                            </th>
                            <th
                              style={{
                                padding: "10px 16px",
                                textAlign: "center",
                                fontWeight: 700,
                                border: "1px solid var(--color-grey, #dee2e6)",
                                width: "200px",
                              }}
                            >
                              {t?.marksObtained || "Marks Obtained"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: "Tamil", field: "tamil_mark" },
                            { label: "English", field: "english_mark" },
                            { label: "Mathematics", field: "mathematics_mark" },
                            { label: "Science", field: "science_mark" },
                            {
                              label: "Social Science",
                              field: "social_science_mark",
                            },
                          ].map(({ label, field }) => (
                            <tr
                              key={field}
                              style={{
                                borderBottom:
                                  "1px solid var(--color-grey, #dee2e6)",
                              }}
                            >
                              <td
                                style={{
                                  padding: "8px 16px",
                                  border:
                                    "1px solid var(--color-grey, #dee2e6)",
                                  fontWeight: 600,
                                }}
                              >
                                {label}
                              </td>
                              <td
                                style={{
                                  padding: "8px 16px",
                                  border:
                                    "1px solid var(--color-grey, #dee2e6)",
                                  textAlign: "center",
                                  color: "var(--text-medium-gray, #555)",
                                }}
                              >
                                100
                              </td>
                              <td
                                style={{
                                  padding: "8px 8px",
                                  border:
                                    "1px solid var(--color-grey, #dee2e6)",
                                }}
                              >
                                <input
                                  type="text"
                                  name={field}
                                  value={formData[field]}
                                  onChange={handleInputChange}
                                  placeholder="0-100"
                                  maxLength={3}
                                  style={{
                                    width: "100%",
                                    padding: "6px 10px",
                                    border: errors[field]
                                      ? "1px solid var(--color-error, #d32f2f)"
                                      : "1px solid var(--color-dark-grey, #ccc)",
                                    borderRadius:
                                      "var(--border-radius-sm, 4px)",
                                    fontSize: "14px",
                                    textAlign: "center",
                                    outline: "none",
                                  }}
                                />
                                {errors[field] && (
                                  <div
                                    style={{
                                      color: "var(--color-error, #d32f2f)",
                                      fontSize: "11px",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {errors[field]}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                          <tr
                            style={{
                              backgroundColor:
                                "var(--color-neutral-150, #f5f5f5)",
                              fontWeight: 700,
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                              }}
                            >
                              {t?.totalMark || "Total Marks"}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                                textAlign: "center",
                              }}
                            >
                              500
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                                textAlign: "center",
                              }}
                            >
                              {formData.total_mark}
                            </td>
                          </tr>
                          <tr
                            style={{
                              backgroundColor:
                                "var(--color-neutral-150, #f5f5f5)",
                              fontWeight: 700,
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                              }}
                            >
                              {t?.percentage || "Percentage"}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                              }}
                            />
                            <td
                              style={{
                                padding: "10px 16px",
                                border: "1px solid var(--color-grey, #dee2e6)",
                                textAlign: "center",
                              }}
                            >
                              {formData.total_percentage.toFixed
                                ? formData.total_percentage.toFixed(2)
                                : formData.total_percentage}{" "}
                              %
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* ══════ PREFERRED COURSE DETAILS ══════ */}
                    <SectionHeader
                      title={
                        t?.preferredCourseDetails || "Preferred Course Details"
                      }
                    />
                    <p
                      style={{
                        padding: "0 0 8px",
                        fontSize: "13px",
                        color: "var(--text-medium-gray, #555)",
                        fontStyle: "italic",
                      }}
                    >
                      {t?.preferredCourseNote ||
                        "Please specify the order of preference for the stream you wish to study."}
                    </p>
                    <div className="row">
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-6 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={
                            t?.preferredStudyCourse ||
                            "Course Preferred to Study"
                          }
                          name="preferred_study_course"
                          value={formData.preferred_study_course}
                          onChange={(value) =>
                            handleDropdownChange(
                              "preferred_study_course",
                              value,
                            )
                          }
                          options={COURSE_PREFERRED_OPTIONS}
                          placeholder={
                            t?.selectCoursePrefered || "Select Course Preferred"
                          }
                          required
                          error={errors.preferred_study_course}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-6 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={
                            t?.yearOfAdmission ||
                            "Year of Admission (First Year / Second Year)"
                          }
                          name="admission_for_which_year"
                          value={formData.admission_for_which_year}
                          onChange={(value) =>
                            handleDropdownChange(
                              "admission_for_which_year",
                              value,
                            )
                          }
                          options={YEAR_OF_ADMISSION_OPTIONS}
                          placeholder={t?.select || "Select"}
                          required
                          error={errors.admission_for_which_year}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-4 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={t?.preferredGroup1 || "Preferred Group 1"}
                          name="preferred_group_1"
                          value={formData.preferred_group_1}
                          onChange={(value) =>
                            handleDropdownChange("preferred_group_1", value)
                          }
                          options={preferredGroupOptions}
                          placeholder={
                            t?.selectPreferredGroup1 ||
                            "Select Preferred Group 1"
                          }
                          required
                          error={errors.preferred_group_1}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-4 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={t?.preferredGroup2 || "Preferred Group 2"}
                          name="preferred_group_2"
                          value={formData.preferred_group_2}
                          onChange={(value) =>
                            handleDropdownChange("preferred_group_2", value)
                          }
                          options={preferredGroupOptions}
                          placeholder={
                            t?.selectPreferredGroup2 ||
                            "Select Preferred Group 2"
                          }
                          error={errors.preferred_group_2}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-4 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={t?.preferredGroup3 || "Preferred Group 3"}
                          name="preferred_group_3"
                          value={formData.preferred_group_3}
                          onChange={(value) =>
                            handleDropdownChange("preferred_group_3", value)
                          }
                          options={preferredGroupOptions}
                          placeholder={
                            t?.selectPreferredGroup3 ||
                            "Select Preferred Group 3"
                          }
                          error={errors.preferred_group_3}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-6 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={
                            t?.studentMotherTongue || "Student Mother Tongue"
                          }
                          name="mother_tongue"
                          value={formData.mother_tongue}
                          onChange={(value) =>
                            handleDropdownChange("mother_tongue", value)
                          }
                          options={motherTongueOptions}
                          placeholder={
                            t?.selectMotherTongue || "Select Mother Tongue"
                          }
                          required
                          error={errors.mother_tongue}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-12 col-md-12 col-sm-12 px-2" : "col-lg-6 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={
                            t?.mediumOfInstructionLeavingSchool ||
                            "Medium of Instruction at the Time of Leaving School"
                          }
                          name="last_medium_of_instruction"
                          value={formData.last_medium_of_instruction}
                          onChange={(value) =>
                            handleDropdownChange(
                              "last_medium_of_instruction",
                              value,
                            )
                          }
                          options={mediumOptions}
                          placeholder={t?.select || "Select"}
                          required
                          error={errors.last_medium_of_instruction}
                        />
                      </div>
                    </div>

                    {/* ══════ PREVIOUS SCHOOL ADDRESS / LAST STUDIED ══════ */}
                    <SectionHeader
                      title={
                        t?.previousSchoolAddress || "Previous School Address"
                      }
                    />
                    <div className="row">
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-6 px-2 col-md-6 col-sm-12" : "col-lg-6 px-2 col-md-6 col-sm-12"}`}
                      >
                        <CustomDropdown
                          label={t?.lastStudiedClass || "Last Studied Class"}
                          name="last_studied_class"
                          value={formData.last_studied_class}
                          onChange={(value) =>
                            handleDropdownChange("last_studied_class", value)
                          }
                          options={classOptions}
                          placeholder={t?.selectClass || "Select Class"}
                          required
                          error={errors.last_studied_class}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDatePicker
                          label={t?.studiedYear || "Year of Study"}
                          name="studied_year"
                          value={
                            formData.studied_year
                              ? dayjs(formData.studied_year)
                              : null
                          }
                          onChange={(date) =>
                            handleMonthYearChange("studied_year", date)
                          }
                          placeholder="---------- ----"
                          picker="month"
                          required
                          error={errors.studied_year}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={
                            t?.lastStudiedSchool ||
                            "Name of the Last Studied School"
                          }
                          name="last_studied_school_name"
                          type="text"
                          value={formData.last_studied_school_name}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus("last_studied_school_name")
                          }
                          placeholder={t?.enterHere || "Enter here.."}
                          required
                          error={errors.last_studied_school_name}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.emisNo || "EMIS No."}
                          name="emis_id"
                          type="text"
                          value={formData.emis_id}
                          onChange={handleInputChange}
                          placeholder="10-digit EMIS number"
                          maxLength={10}
                          required
                          error={errors.emis_id}
                          disabled={formData.current_status === "Admitted"}
                        />
                      </div>
                      <div
                        className="col-lg-12 px-2 col-md-12 col-sm-12"
                        style={{ marginBottom: "16px" }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontWeight: 600,
                            fontSize: "13px",
                            marginBottom: "6px",
                            color: "var(--text-dark, #333)",
                          }}
                        >
                          {t?.reasonForLeaving ||
                            "Reason for Leaving or Discontinuation"}
                          <span style={{ color: "#d32f2f", marginLeft: "2px" }}>
                            *
                          </span>
                        </label>
                        <textarea
                          name="reason_for_discontinuing"
                          value={formData.reason_for_discontinuing}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus("reason_for_discontinuing")
                          }
                          placeholder={
                            t?.reasonForLeavingPlaceholder ||
                            "Enter reason for leaving or discontinuation"
                          }
                          rows={3}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: errors.reason_for_discontinuing
                              ? "1px solid var(--color-error, #d32f2f)"
                              : "1px solid var(--color-dark-grey, #ccc)",
                            borderRadius: "var(--border-radius-button, 6px)",
                            fontSize: "14px",
                            resize: "vertical",
                            outline: "none",
                            fontFamily: "inherit",
                          }}
                        />
                        {errors.reason_for_discontinuing && (
                          <div
                            style={{
                              color: "var(--color-error, #d32f2f)",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {errors.reason_for_discontinuing}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ══════ CERTIFICATES FOR CO-CURRICULAR ══════ */}
                    <SectionHeader
                      title={
                        t?.certificatesForCoCurricular ||
                        "Certificates for Co-curricular Activities"
                      }
                    />
                    <div className="row">
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomRadioGroup
                          label={
                            t?.nccNssScoutsGuides ||
                            "NCC / NSS / Scouts / Guides / Red Cross – Would you like to join?"
                          }
                          name="willing_to_join_services"
                          value={formData.willing_to_join_services}
                          onChange={(value) =>
                            handleRadioChange("willing_to_join_services", value)
                          }
                          options={YES_NO_OPTIONS}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomRadioGroup
                          label={
                            t?.smallpoxVaccinationTaken ||
                            "Smallpox Vaccination Taken?"
                          }
                          name="is_vaccinated_pox"
                          value={formData.is_vaccinated_pox}
                          onChange={(value) =>
                            handleRadioChange("is_vaccinated_pox", value)
                          }
                          options={YES_NO_OPTIONS}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomFileInput
                          label={
                            t?.certificatesForCoCurricular ||
                            "Certificates for Co-curricular Activities"
                          }
                          name="certificate_path"
                          value={formData.certificate_original_name || null}
                          onChange={handleCoCurricularFileChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          placeholder={
                            formData.certificate_original_name ||
                            t?.noFileChosen ||
                            "No file chosen"
                          }
                          buttonText={t?.chooseFile || "Choose File"}
                          error={errors.certificate_path}
                        />
                        {/* Preview Button */}
                        {formData?.certificate_path && (
                          <button
                            type="button"
                            className={styles.viewBtn}
                            onClick={() =>
                              handlePreview(formData.certificate_path)
                            }
                          >
                            <FaEye />
                          </button>
                        )}
                        <span className={styles.fileHint}>
                          {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG."}
                        </span>
                      </div>
                    </div>

                    {/* ══════ OTHER DETAILS ══════ */}
                    <SectionHeader title={t?.otherDetails || "Other Details"} />
                    <div className="row">
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.place || "Place"}
                          name="place"
                          type="text"
                          value={formData.place}
                          onChange={handleInputChange}
                          onFocus={() => handleTamilInputFocus("place")}
                          placeholder={t?.enterHere || "Enter here.."}
                          required
                          error={errors.place}
                        />
                      </div>
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomFileInput
                          label={t?.studentImage || "Student Image"}
                          name="student_image"
                          value={formData.student_image_original_name || null}
                          onChange={handleStudentImageChange}
                          accept="image/*"
                          placeholder={
                            formData.student_image_original_name ||
                            t?.noFileChosen ||
                            "No file chosen"
                          }
                          buttonText={t?.chooseFile || "Choose Image"}
                          required
                          error={errors.student_image}
                        />
                        {/* Preview Button */}
                        {formData?.student_image && (
                          <button
                            type="button"
                            className={styles.viewBtn}
                            onClick={() =>
                              handlePreview(formData.student_image)
                            }
                          >
                            <FaEye />
                          </button>
                        )}
                        <span className={styles.fileHint}>
                          {t?.allowedJpgPng || "Allowed: JPG/PNG images."}
                        </span>
                      </div>
                    </div>

                    {/* ══════ STATUS DETAILS ══════ */}
                    <SectionHeader
                      title={t?.statusDetails || "Status Details"}
                    />
                    <div className="row">
                      {!showSeatAllocationFields && (
                        <>
                          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                            <CustomTextInput
                              label={t?.classLabel || "Class"}
                              name="assigned_class"
                              type="text"
                              value={formData.assigned_class || "-"}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                            <CustomTextInput
                              label={t?.group || "Group"}
                              name="assigned_group"
                              type="text"
                              value={formData.assigned_group || "-"}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                            <CustomTextInput
                              label={t?.section || "Section"}
                              name="assigned_section"
                              type="text"
                              value={formData.assigned_section || "-"}
                              disabled
                              readOnly
                            />
                          </div>
                        </>
                      )}

                      {(showSeatAllocationFields ||
                        formData.current_status === "Seat Allocated" ||
                        formData.current_status === "Interview Scheduled" ||
                        formData.current_status === "Invite Not Sent" ||
                        formData.current_status === "Approved") && (
                        <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                          <CustomDropdown
                            label={
                              showSeatAllocationFields
                                ? t?.selectGroupClassSection ||
                                  "Select Group/Class/Section"
                                : t?.changeSectionOptional ||
                                  "Change Section (Optional)"
                            }
                            name="change_section"
                            value={formData.change_section}
                            onChange={(selectedOption) => {
                              const value =
                                selectedOption?.value || selectedOption;
                              handleDropdownChange("change_section", value);
                            }}
                            options={
                              sectionOptions.length > 0
                                ? sectionOptions
                                : [{ value: "", label: t?.select || "Select" }]
                            }
                            placeholder={t?.select || "Select"}
                          />
                        </div>
                      )}

                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomTextInput
                          label={t?.currentStatus || "Current Status"}
                          name="current_status"
                          type="text"
                          value={formData.current_status || "-"}
                          disabled
                          readOnly
                        />
                      </div>
                      <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                        <CustomDropdown
                          label={t?.status || "Status"}
                          name="status"
                          value={formData.status || ""}
                          onChange={(v) => handleDropdownChange("status", v)}
                          options={getStatusOptions(formData.current_status)}
                          placeholder={
                            t?.selectStatus || "Select Current Status"
                          }
                          error={errors.status}
                          disabled={shouldDisableStatus(
                            formData.current_status,
                          )}
                        />
                      </div>

                      {/* ── Feedback: shown only when status is Rejected or Approved ── */}
                      {(() => {
                        const selVal = formData.status?.value
                          ? formData.status.value
                          : formData.status;
                        return selVal === "Rejected" || selVal === "Approved";
                      })() && (
                        <div className="col-12 px-2 mb-3">
                          <CustomTextInput
                            label={t?.feedback || "Feedback"}
                            name="feedback"
                            type="text"
                            value={formData.feedback}
                            onChange={handleInputChange}
                            onFocus={() => handleTamilInputFocus("feedback")}
                            placeholder={t?.enterHere || "Enter here.."}
                            required
                            error={errors.feedback}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        {/* ── FOOTER ── */}
        <Modal.Footer className={styles.modalFooter}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.backButton}
            onClick={handleClose}
            fullWidth={false}
          >
            {t?.back || "BACK"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.updateButton}
            onClick={handleUpdateChanges}
            fullWidth={false}
            loading={submitting}
            disabled={formData.current_status === "Withdraw"}
          >
            {t?.updateChanges || "UPDATE CHANGES"}
          </CustomButton>
        </Modal.Footer>
      </Modal>

      {/* ── POPUP MODAL ── */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        message={modalState.message}
        onClose={() => setModalState({ open: false, type: "", message: "" })}
      />
    </div>
  );
};

export default HigherSecondaryAdmissionEditModal;
